import React from "react";
import { Card } from "react-bootstrap";
import ReportTableFrame from "@core/components/ReportTableFrame";
import type {
  DistrictInHouseBlock,
  IncomeBreakdownRow,
  MoneyLeaf,
} from "@core/reports/api/fetchFinanceReport";

/**
 * Church-scope read-only income tables (Tabs 1 & 2). Single church, one row per
 * income source — no aggregation (the District/Nation pivots live in
 * IncomePivotTable). calculation_basis / category_name / rule_name are excluded (D5).
 * Shares the report-table skin (`.ntcog-report-*`) with ReportTreeTable.
 */

function money(leaf: MoneyLeaf | undefined): string {
  const v = leaf?.value;
  if (v === null || v === undefined) return "—";
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const ChurchIncomeBreakdownTable: React.FC<{ rows: IncomeBreakdownRow[] }> = ({ rows }) => (
  <Card className="ntcog-card">
    <Card.Header className="fw-semibold">Financial Breakdown</Card.Header>
    <Card.Body className="p-0">
      <ReportTableFrame>
        <table className="ntcog-report-table" style={{ minWidth: 720 }}>
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col">Total Raised</th>
              <th scope="col">To Island Office</th>
              <th scope="col">To District Office</th>
              <th scope="col">Total Out</th>
              <th scope="col">To Church</th>
            </tr>
          </thead>
          <tbody>
            {(rows ?? []).map((row) => (
              <tr key={row.kpi_id}>
                <td>{row.source}</td>
                <td>{money(row.total_raised)}</td>
                <td>{money(row.total_to_island_office)}</td>
                <td>{money(row.total_to_district_office)}</td>
                <td>{money(row.total_out)}</td>
                <td className="fw-semibold">{money(row.total_to_church)}</td>
              </tr>
            ))}
            {(rows ?? []).length === 0 ? (
              <tr><td colSpan={6} className="text-center text-muted py-3">No income recorded for this period.</td></tr>
            ) : null}
          </tbody>
        </table>
      </ReportTableFrame>
    </Card.Body>
  </Card>
);

export const ChurchDistrictInHouseTable: React.FC<{ block: DistrictInHouseBlock }> = ({ block }) => (
  <Card className="ntcog-card">
    <Card.Header className="fw-semibold">District In-House Memo</Card.Header>
    <Card.Body className="p-0">
      <ReportTableFrame>
        <table className="ntcog-report-table" style={{ minWidth: 480 }}>
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col">To Island Office</th>
              <th scope="col">To District Office</th>
            </tr>
          </thead>
          <tbody>
            {(block?.rows ?? []).map((row) => (
              <tr key={row.kpi_id}>
                <td>{row.source}</td>
                <td>{money(row.funds_to_island_office)}</td>
                <td>{money(row.funds_to_district_office)}</td>
              </tr>
            ))}
            {block?.total_funds_sent_to_district_overseer ? (
              <tr className="ntcog-rt-row--total">
                <td>Total Funds Sent to District Overseer</td>
                <td>{money(block.total_funds_sent_to_district_overseer.funds_to_island_office)}</td>
                <td>{money(block.total_funds_sent_to_district_overseer.funds_to_district_office)}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </ReportTableFrame>
      {Array.isArray(block?.summary) && block.summary.length > 0 ? (
        <div className="px-3 py-2 border-top small">
          {block.summary.map((s, i) => (
            <div key={i} className="d-flex justify-content-between">
              <span className="text-muted">{s.label}</span>
              <span className="fw-semibold">{money(s.value)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </Card.Body>
  </Card>
);
